import React, { useState, useEffect, useRef } from 'react';
import '../css/AITrainManager.css';
import Sidebar from './side/Sidebar';
import axios from 'axios';
import { SERVER_URL } from '../../config/config';
import { useNavigate } from 'react-router-dom';
import ErrorPopup from './side/ErrorPopup';

const defaultParams = {
  learning_rate: '0.01',
  batch_size: '64',
  epoch_number: '256',
  input_size: '512',
  hidden_size: '64',
  num_layers: '3',
};

const paramDescriptions = {
  learning_rate: '학습률(learning rate): 모델이 가중치를 업데이트하는 비율로, 너무 크면 발산, 너무 작으면 수렴이 느려집니다.',
  batch_size: '배치 사이즈(batch size): 한 번에 학습에 사용하는 데이터 샘플의 개수입니다.',
  epoch_number: '에폭 수(epoch): 전체 데이터셋을 몇 번 반복 학습할지 결정합니다.',
  input_size: '입력 사이즈(input size): 입력 데이터의 차원(특성 수)입니다.',
  hidden_size: '은닉 사이즈(hidden size): 은닉층의 뉴런(노드) 개수입니다.',
  num_layers: '레이어 수(num layers): 신경망의 층(레이어) 개수입니다.',
};

const AITrainManager = () => {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [params, setParams] = useState(defaultParams);
  const [isTraining, setIsTraining] = useState(false);
  const isTrainingRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('대기 중');
  const [showDatasetSection, setShowDatasetSection] = useState(false);
  const [showParamSection, setShowParamSection] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [descPopup, setDescPopup] = useState({ open: false, key: '' });
  const [showVersionPopup, setShowVersionPopup] = useState(false);
  const [versionInput, setVersionInput] = useState('');
  const [keyUpdatesInput, setKeyUpdatesInput] = useState('');
  const [latestVersion, setLatestVersion] = useState('');
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);

  useEffect(() => {
    fetchDatasets();
    fetchLatestVersion();
  }, []);

  const fetchDatasets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER_URL}/admin/dataset/all`);
      if (response.data.result_code !== 201) {
        throw new Error('Failed to fetch datasets');
      }
      setDatasets(response.data.data || []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchLatestVersion = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/admin/dataset/latest-version`);
      if (response.data.result_code === 201) {
        setLatestVersion(response.data.data || '없음');
      }
    } catch (err) {
      console.error('Failed to fetch latest version:', err);
    }
  };

  const handleDatasetToggle = (id) => {
    setSelectedDatasets((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleAllToggle = (checked) => {
    setSelectedDatasets(checked ? datasets.map((d) => d.datasetId) : []);
  };

  const handleParamChange = (e) => {
    const { name, value } = e.target;
    setParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleStart = async () => {
    if (selectedDatasets.length === 0) return;
    setShowVersionPopup(true);
  };

  const handleVersionSubmit = async () => {
    if (!versionInput.trim() || !keyUpdatesInput.trim()) {
      alert('버전명과 주요 업데이트 내용을 모두 입력해주세요.');
      return;
    }

    setIsTraining(true);
    isTrainingRef.current = true;
    setShowVersionPopup(false);
    setShowDatasetSection(false);
    setShowParamSection(false);
    setStatus('학습 요청 중...');
    setProgress(0);

    try {
      const adminId = localStorage.getItem('adminId');
      const payload = {
        adminId: adminId,
        trainDatasetIds: selectedDatasets,
        version: versionInput,
        keyUpdates: keyUpdatesInput,
        params: {
          learningRate: parseFloat(params.learning_rate),
          batchSize: parseInt(params.batch_size),
          epochNumber: parseInt(params.epoch_number),
          inputSize: parseInt(params.input_size),
          hiddenSize: parseInt(params.hidden_size),
          numLayers: parseInt(params.num_layers)
        }
      };
      const response = await axios.post(`${SERVER_URL}/admin/ai/train`, payload);
      
      setStatus('학습 중...');
      const trainId = response.data;
      console.log("trainId", trainId);

      const checkProgress = async () => {
        console.log("isTraining", isTrainingRef.current);
        if (!isTrainingRef.current) {
          return;
        }
        
        try {
          const progressResponse = await axios.get(`${SERVER_URL}/admin/ai/train/progress`, {
            params: { train_id: trainId }
          }); 
          
          const { epoch, total_epochs } = progressResponse.data;
          const progress = total_epochs === 0 ? 0 : Math.round((epoch / total_epochs) * 100);
          setProgress(progress);
          
          if (progress >= 100) {
            setIsTraining(false);
            isTrainingRef.current = false;
            setStatus('학습 완료!');
            setShowCompletionPopup(true);
          } else {
            setTimeout(checkProgress, 1000);
          }
        } catch (error) {
          console.error('Progress check failed:', error);
          setIsTraining(false);
          isTrainingRef.current = false;
          setStatus('학습 중 오류 발생');
        }
      };

      setTimeout(checkProgress, 1000);
    } catch (err) {
      setIsTraining(false);
      isTrainingRef.current = false;
      setStatus('학습 요청 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCancel = () => {
    setIsTraining(false);
    isTrainingRef.current = false;
    setStatus('대기 중');
    setProgress(0);
    setShowCancelPopup(true);
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="content-wrapper">
          <Sidebar />
          <div className="main-content">
            <div className="loading">데이터셋을 불러오는 중...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management">
        <div className="content-wrapper">
          <Sidebar />
          <div className="main-content">
            <div className="error">Error: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="content-wrapper">
        <Sidebar />
        <div className="main-content">
          <div className="detail-header">
            <h1>AI 학습 관리</h1>
          </div>

          {/* 데이터셋 선택 영역 */}
          <div className="detail-content" style={{padding: '20px' }}>
            <div className="detail-row" style={{ cursor: 'pointer' }} onClick={() => setShowDatasetSection(v => !v)}>
              <div className="detail-label" style={{ fontSize: '22px', display: 'flex',  width: '100%', alignItems: 'center' }}>
                <span style={{ color: '#808080', fontSize: '1em', marginRight: 8 }}>
                  {showDatasetSection ? '▼' : '▶'}
                </span>
                데이터셋 선택
              </div>
            </div>
            {showDatasetSection && (
              <div className="table-container">
                <div className="header-row">
                  <span style={{ width: '10%' }}>
                    <input
                      type="checkbox"
                      checked={selectedDatasets.length === datasets.length && datasets.length > 0}
                      onChange={(e) => handleAllToggle(e.target.checked)}
                    />
                  </span>
                  <span style={{ width: '40%' }}>이름</span>
                  <span style={{ width: '25%' }}>크기</span>
                  <span style={{ width: '25%' }}>업로더</span>
                </div>
                <div className="data-entries">
                  {datasets.map((ds) => (
                    <div key={ds.datasetId} className="table-row">
                      <span className="cell" style={{ width: '10%' }}>
                        <input
                          type="checkbox"
                          checked={selectedDatasets.includes(ds.datasetId)}
                          onChange={() => handleDatasetToggle(ds.datasetId)}
                        />
                      </span>
                      <span className="cell" style={{ width: '40%', justifyContent: 'left' }}>{ds.name}</span>
                      <span className="cell" style={{ width: '30%', justifyContent: 'left' }}>{ds.size ? ds.size.toFixed(2) + ' GB' : '-'}</span>
                      <span className="cell" style={{ width: '30%' }}>{ds.uploaderName || '알 수 없음'}</span>
                    </div>
                  ))}
                </div>
                <div className="inquiry-answer-button-group" style={{ display: 'flex', justifyContent: 'left', alignItems: 'center', width: '100%', marginBottom: '10px'  }}>
                  <button className="button" style={{ marginLeft: '20px' ,  fontWeight: 400, fontSize: '16px', padding: '8px 16px'}} onClick={() => handleAllToggle(true)}>전체 선택</button>
                  <button className="back-button" onClick={() => handleAllToggle(false)}>전체 해제</button>
                </div>
              </div>
            )}
          </div>

          {/* 하이퍼파라미터 영역 */}
          <div className="detail-content" style={{padding: '20px' }}>
            <div className="detail-row" style={{ cursor: 'pointer' }} onClick={() => setShowParamSection(v => !v)}>
              <div className="detail-label" style={{ fontSize: '22px', display: 'flex', width: '100%', alignItems: 'center'}}>
                <span style={{ color: '#808080', fontSize: '1em', marginRight: 8 }}>
                  {showParamSection ? '▼' : '▶'}
                </span>
                하이퍼파라미터
              </div>
            </div>
            {showParamSection && (
              <div className="table-container">
                <div className="data-entries">
                  {Object.keys(params).map((key) => (
                    <div key={key} className="table-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                      <div className="cell" style={{ width: '50%', display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '50px' }}>
                        {key}
                        <span
                          className="param-help-icon"
                          style={{ cursor: 'pointer', color: '#917FB3', fontWeight: 'bold', fontSize: '1.1em' }}
                          onClick={() => setDescPopup({ open: true, key })}
                          title="설명 보기"
                        >
                          ?
                        </span>
                      </div>
                      <div className="cell" style={{ width: '50%' }}>
                        <input
                          type="text"
                          name={key}
                          value={params[key]}
                          onChange={handleParamChange}
                          style={{ width: '70%', padding: '10px', textAlign: 'center', borderRadius: '8px', border: '1px solid #ddd' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 하이퍼파라미터 설명 팝업 */}
          {descPopup.open && (
            <div className="param-desc-modal-overlay" onClick={() => setDescPopup({ open: false, key: '' })}>
              <div className="param-desc-modal" onClick={e => e.stopPropagation()}>
                <div className="param-desc-title">{descPopup.key}</div>
                <div className="param-desc-content">{paramDescriptions[descPopup.key]}</div>
                <button className="param-desc-close-btn" onClick={() => setDescPopup({ open: false, key: '' })}>닫기</button>
              </div>
            </div>
          )}

          <div className="train-inquiry-answer-button-group">
            <div className="train-status">
              <div className="train-status-label">상태: {status}</div>
              <div className="train-progress-bar-wrapper">
                <div className="train-progress-bar-bg">
                  <div
                    className="train-progress-bar-fill"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <span className="train-progress-text">{progress}%</span>
              </div>
            </div>
            <div className="train-button-group">
              <button
                className="train-answer-button"
                onClick={handleStart}
                disabled={isTraining || selectedDatasets.length === 0}
              >
                학습 시작
              </button>
              <button
                className="train-back-button"
                onClick={handleCancel}
                disabled={!isTraining}
              >
                취소
              </button>
            </div>
          </div>

          {/* Version Input Popup */}
          {showVersionPopup && (
            <div className="version-popup-overlay" onClick={() => setShowVersionPopup(false)}>
              <div className="version-popup" onClick={e => e.stopPropagation()}>
                <h2>버전 정보 입력</h2>
                <div className="latest-version-info">
                  최신 버전: {latestVersion}
                </div>
                <div className="version-input-group">
                  <label>새 버전명:</label>
                  <input
                    type="text"
                    value={versionInput}
                    onChange={(e) => setVersionInput(e.target.value)}
                    placeholder="예: v1.0.0"
                  />
                </div>
                <div className="version-input-group">
                  <label>주요 업데이트 내용:</label>
                  <textarea
                    value={keyUpdatesInput}
                    onChange={(e) => setKeyUpdatesInput(e.target.value)}
                    placeholder="주요 업데이트 내용을 입력하세요"
                    rows="4"
                  />
                </div>
                <div className="inquiry-answer-button-group">
                  <button className="answer-button" style={{position: 'static', marginRight: '5px'}} onClick={handleVersionSubmit}>확인</button>
                  <button className="back-button" onClick={() => setShowVersionPopup(false)}>취소</button>
                </div>
              </div>
            </div>
          )}

          {/* Completion Popup */}
          {showCompletionPopup && (
            <ErrorPopup
              message="모델 학습이 완료되었습니다."
              onClose={() => {
                setShowCompletionPopup(false);
                navigate('/ai-models/versions');
              }}
            />
          )}

          {/* Cancel Popup */}
          {showCancelPopup && (
            <ErrorPopup
              message="학습이 취소되었습니다."
              onClose={() => setShowCancelPopup(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AITrainManager; 